import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type {
  AppTheme,
  CalendarPreferences,
  CalendarStyleId,
  CanvasBackgroundKey,
  ClientUserPreferences,
  CosmeticRarity,
  FocusSoundscapeKey,
} from '../../types/schema'
import { useClientPreferences } from '../../context/useClientPreferences'
import { useGamification } from '../../context/useGamification'
import { useAuth } from '../../context/useAuth'
import { WorkspaceContext } from '../../context/workspaceContextCore'
import { switchedOffModulesFor } from '../../utils/workspaceModules'
import { useNls } from '../../nls/nlsProvider'
import {
  DEFAULT_UNLOCKS,
  RARITY_UNLOCK_LEVEL,
} from '../../utils/cosmeticCatalog'
import { FOCUS_GUARDIANS } from '../../utils/gamificationRegistry'
import { THEME_CATALOG, type ThemeCatalogEntry } from '../../utils/themeCatalog'
import { THEME_KEY_TO_COSMETIC_ID } from '../../assets/cosmetics'
import { CALENDAR_STYLES, normalizeCalendarStyleId } from '../Calendar/calendarThemes'
import {
  subscribeToCalendarPreferences,
  updateCalendarPreferences,
} from '../../services/calendarPreferencesService'
import {
  SettingsSection,
  SettingsRow,
  SettingsChoiceGrid,
  SettingsRadioList,
  useInstantSettingRows,
  Toggle,
  type SettingsRadioOption,
} from '../Common'
import { Pressable } from '../Common/Pressable'
import {
  isFreeCanvasBackground,
  isFreeFocusSoundscape,
} from '../../utils/workspaceModulesCosmetics'
import './AppearancePanel.memberMobile.css'

type ThemeOption = ThemeCatalogEntry

const THEME_OPTIONS: ThemeOption[] = THEME_CATALOG

type BackgroundOption = {
  key: CanvasBackgroundKey
  label: string
  description: string
  rarity?: CosmeticRarity
  cosmeticId?: string
}

const BACKGROUND_OPTIONS: BackgroundOption[] = [
  {
    key: 'BLUEPRINT_GRID',
    label: 'Blueprint Grid',
    description: 'Clean engineering grid.',
    rarity: 'COMMON',
    cosmeticId: 'bg_grid',
  },
  {
    key: 'NEBULA',
    label: 'Nebula',
    description: 'Drifting cosmic clouds.',
    rarity: 'RARE',
    cosmeticId: 'bg_nebula',
  },
  {
    key: 'CIRCUIT',
    label: 'Circuit',
    description: 'Pulsing neon traces.',
    rarity: 'EPIC',
    cosmeticId: 'bg_circuit',
  },
]

type SoundscapeOption = {
  key: FocusSoundscapeKey
  label: string
  description: string
  rarity?: CosmeticRarity
  cosmeticId?: string
  guardianAvatarId?: string
}

const SOUNDSCAPE_OPTIONS: SoundscapeOption[] = [
  { key: 'SILENCE', label: 'Silence', description: 'A breathing void.' },
  {
    key: 'LOFI_RAIN',
    label: 'Lo-Fi Rain + Vinyl',
    description: 'The Alchemist soundscape.',
    rarity: 'EPIC',
    cosmeticId: FOCUS_GUARDIANS.ALCHEMIST.audioId,
    guardianAvatarId: 'avatar_alchemist',
  },
  {
    key: 'ALPHA_WAVE',
    label: 'Alpha-Wave + Singing Bowl',
    description: 'The Zen Master soundscape.',
    rarity: 'LEGENDARY',
    cosmeticId: FOCUS_GUARDIANS.ZEN_MASTER.audioId,
    guardianAvatarId: 'avatar_zen_master',
  },
]

const animationLabels: Record<
  ClientUserPreferences['animationProfile'],
  string
> = {
  FLUID: 'Fluid',
  FAST: 'Fast',
  REDUCED_MOTION: 'Reduced Motion',
}

type LockedPreviewTarget = {
  kind: 'THEME' | 'BACKGROUND' | 'SOUNDSCAPE'
  cosmeticId: string
}

type AppearancePanelProps = {
  onPreviewLockedItem?: (target: LockedPreviewTarget) => void
  /** Member settings promotes the mobile theme without changing desktop ordering. */
  memberMobile?: boolean
}

const useSafeAuthUser = (): { uid: string } | null => {
  try {
    const auth = useAuth()
    return auth?.user?.uid ? { uid: auth.user.uid } : null
  } catch {
    return null
  }
}

export const AppearancePanel = ({
  onPreviewLockedItem,
  memberMobile = false,
}: AppearancePanelProps) => {
  const { t } = useNls()
  const user = useSafeAuthUser()
  const { preferences, updatePreferences } = useClientPreferences()
  const { profile: gamificationProfile, equip } = useGamification()
  const workspaceContext = useContext(WorkspaceContext)
  const activeWorkspace = workspaceContext?.workspaces.find((w) => w.id === workspaceContext.activeWorkspaceId) ?? null
  const funPackOff = activeWorkspace ? switchedOffModulesFor(activeWorkspace).has('funPack') : false

  const instantRows = useInstantSettingRows()

  const runSave = (key: string, fn: () => Promise<unknown> | void) => {
    void instantRows.run(key, fn)
  }

  const [calendarPreferences, setCalendarPreferences] = useState<Partial<CalendarPreferences>>({})

  useEffect(() => {
    if (!user?.uid) return
    const unsubscribe = subscribeToCalendarPreferences(user.uid, (incoming) => {
      setCalendarPreferences(incoming)
    })
    return () => unsubscribe()
  }, [user?.uid])

  const effectiveCalendarStyle = normalizeCalendarStyleId(calendarPreferences.styleId)
  const effectiveBoardScopeMode = calendarPreferences.boardScope?.mode ?? 'current'

  const calendarStyleOptions: SettingsRadioOption[] = useMemo(
    () =>
      CALENDAR_STYLES.map((style) => ({
        value: style.id,
        label: style.label,
        hint:
          style.id === 'cards'
            ? t('NLSSettingsCalendarStyleCardsHint')
            : t('NLSSettingsCalendarStyleGlassHint'),
      })),
    [t],
  )

  const calendarBoardScopeOptions: SettingsRadioOption[] = useMemo(
    () => [
      {
        value: 'current',
        label: t('NLSSettingsCalendarBoardScopeCurrent'),
        hint: t('NLSSettingsCalendarBoardScopeCurrentHint'),
      },
      {
        value: 'custom',
        label: t('NLSSettingsCalendarBoardScopeCustom'),
        hint: t('NLSSettingsCalendarBoardScopeCustomHint'),
      },
    ],
    [t],
  )

  const handleCalendarStyleSelect = (nextStyle: string) => {
    const styleId = nextStyle as CalendarStyleId
    setCalendarPreferences((current) => ({ ...current, styleId }))
    const uid = user?.uid
    if (!uid) return
    void runSave('calendarStyle', () =>
      updateCalendarPreferences(uid, { styleId }),
    )
  }

  const handleCalendarBoardScopeSelect = (nextMode: string) => {
    const mode = nextMode as 'current' | 'custom'
    setCalendarPreferences((current) => ({
      ...current,
      boardScope: {
        mode,
        boardKeys: current.boardScope?.boardKeys ?? [],
      },
    }))
    const uid = user?.uid
    if (!uid) return
    void runSave('calendarBoardScope', () =>
      updateCalendarPreferences(uid, {
        boardScope: {
          mode,
          boardKeys: calendarPreferences.boardScope?.boardKeys ?? [],
        },
      }),
    )
  }

  const visibleThemeOptions = useMemo(
    () => (funPackOff ? THEME_OPTIONS.filter((opt) => !opt.cosmeticId) : THEME_OPTIONS),
    [funPackOff],
  )
  const visibleBackgroundOptions = useMemo(
    () => (funPackOff ? BACKGROUND_OPTIONS.filter((o) => isFreeCanvasBackground(o.key)) : BACKGROUND_OPTIONS),
    [funPackOff],
  )
  const visibleSoundscapeOptions = useMemo(
    () => (funPackOff ? SOUNDSCAPE_OPTIONS.filter((o) => isFreeFocusSoundscape(o.key)) : SOUNDSCAPE_OPTIONS),
    [funPackOff],
  )
  const level = gamificationProfile?.level ?? 1

  const unlocks = useMemo(() => {
    const themeIds = new Set<string>([
      ...DEFAULT_UNLOCKS.themes,
      ...(gamificationProfile?.unlockedThemeIds ?? []),
    ])
    const backgroundIds = new Set<string>([
      ...DEFAULT_UNLOCKS.backgrounds,
      ...(gamificationProfile?.unlockedBackgroundIds ?? []),
    ])
    const avatarIds = new Set<string>([
      ...DEFAULT_UNLOCKS.avatars,
      ...(gamificationProfile?.unlockedAvatarIds ?? []),
    ])
    return { themeIds, backgroundIds, avatarIds }
  }, [gamificationProfile])

  const isThemeUnlocked = useCallback((option: ThemeOption): boolean => {
    if (!option.cosmeticId) return true
    if (unlocks.themeIds.has(option.cosmeticId)) return true
    if (option.rarity && level >= RARITY_UNLOCK_LEVEL[option.rarity])
      return true
    return false
  }, [unlocks.themeIds, level])

  const isBackgroundUnlocked = useCallback((option: BackgroundOption): boolean => {
    if (!option.cosmeticId) return true
    if (unlocks.backgroundIds.has(option.cosmeticId)) return true
    if (option.rarity && level >= RARITY_UNLOCK_LEVEL[option.rarity])
      return true
    return false
  }, [unlocks.backgroundIds, level])

  const isSoundscapeUnlocked = useCallback((option: SoundscapeOption): boolean => {
    if (!option.cosmeticId) return true
    if (
      option.guardianAvatarId &&
      unlocks.avatarIds.has(option.guardianAvatarId)
    )
      return true
    return false
  }, [unlocks.avatarIds])

  const handleThemeSelect = (option: ThemeOption) => {
    if (!isThemeUnlocked(option)) {
      if (option.cosmeticId)
        onPreviewLockedItem?.({ kind: 'THEME', cosmeticId: option.cosmeticId })
      return
    }
    void runSave('theme', async () => {
      await updatePreferences({ theme: option.key })
      const equippedThemeId =
        option.cosmeticId ?? THEME_KEY_TO_COSMETIC_ID[option.key] ?? ''
      await equip({ equippedThemeId }).catch((error) => console.warn('[AppearancePanel] equip failed', error))
    })
  }

  const setMemberTheme = (memberAppTheme: AppTheme | null) =>
    void runSave('memberAppTheme', () => updatePreferences({ memberAppTheme }))

  const handleMemberThemeSelect = (option: ThemeOption) => {
    if (!isThemeUnlocked(option)) {
      if (option.cosmeticId)
        onPreviewLockedItem?.({ kind: 'THEME', cosmeticId: option.cosmeticId })
      return
    }
    setMemberTheme(option.key)
  }

  const handleBackgroundSelect = (option: BackgroundOption) => {
    if (!isBackgroundUnlocked(option)) {
      if (option.cosmeticId)
        onPreviewLockedItem?.({
          kind: 'BACKGROUND',
          cosmeticId: option.cosmeticId,
        })
      return
    }
    void runSave('canvasBackground', async () => {
      await updatePreferences({ canvasBackground: option.key })
      await equip({ equippedCanvasBgId: option.cosmeticId ?? '' }).catch((error) => console.warn('[AppearancePanel] equip failed', error))
    })
  }

  const handleSoundscapeSelect = (option: SoundscapeOption) => {
    if (!isSoundscapeUnlocked(option)) {
      if (option.cosmeticId)
        onPreviewLockedItem?.({
          kind: 'SOUNDSCAPE',
          cosmeticId: option.cosmeticId,
        })
      return
    }
    void runSave('focusSoundscape', async () => {
      await updatePreferences({ focusSoundscape: option.key })
      await equip({ equippedAudioId: option.cosmeticId ?? '' }).catch((error) => console.warn('[AppearancePanel] equip failed', error))
    })
  }

  const themeChoiceOptions = useMemo(() => {
    return visibleThemeOptions.map((opt) => ({
      id: opt.key,
      label: opt.label,
      description: opt.description,
      locked: !isThemeUnlocked(opt),
      rarity: opt.rarity,
    }))
  }, [visibleThemeOptions, isThemeUnlocked])

  const memberThemeChoiceOptions = useMemo(() => {
    return [
      {
        id: 'FOLLOW_CONSOLE',
        label: t('NLSSettingsThemeFollowConsole'),
        description: t('NLSSettingsThemeFollowConsoleDesc'),
      },
      ...visibleThemeOptions.map((opt) => ({
        id: `member-${opt.key}`,
        label: opt.label,
        description: opt.description,
        locked: !isThemeUnlocked(opt),
        rarity: opt.rarity,
      })),
    ]
  }, [visibleThemeOptions, isThemeUnlocked, t])

  const backgroundChoiceOptions = useMemo(() => {
    return visibleBackgroundOptions.map((opt) => ({
      id: opt.key,
      label: opt.label,
      description: opt.description,
      locked: !isBackgroundUnlocked(opt),
      rarity: opt.rarity,
    }))
  }, [visibleBackgroundOptions, isBackgroundUnlocked])

  const soundscapeChoiceOptions = useMemo(() => {
    return visibleSoundscapeOptions.map((opt) => ({
      id: opt.key,
      label: opt.label,
      description: opt.description,
      locked: !isSoundscapeUnlocked(opt),
      rarity: opt.rarity,
    }))
  }, [visibleSoundscapeOptions, isSoundscapeUnlocked])

  return (
    <div className={`skSectionStack${memberMobile ? ' appearancePanel--memberMobile' : ''}`}>
      {/* 1. Theme Section */}
      <SettingsSection title={t('NLSSettingsSectionTheme')}>
        <SettingsRow
          stacked
          label={t('NLSSettingsLabelCanvasTheme')}
          hint={t('NLSSettingsHintCanvasTheme')}
          status={instantRows.statusFor('theme')}
          control={
            <SettingsChoiceGrid
              options={themeChoiceOptions}
              value={preferences.theme}
              onChange={(id) => {
                const opt = visibleThemeOptions.find((o) => o.key === id)
                if (opt) handleThemeSelect(opt)
              }}
              onPreviewLocked={(choice) => {
                const opt = visibleThemeOptions.find((o) => o.key === choice.id)
                if (opt?.cosmeticId)
                  onPreviewLockedItem?.({ kind: 'THEME', cosmeticId: opt.cosmeticId })
              }}
            />
          }
        />

        <SettingsRow
          stacked
          label={t('NLSSettingsLabelMemberAppTheme')}
          hint={t('NLSSettingsHintMemberAppTheme')}
          status={instantRows.statusFor('memberAppTheme')}
          control={
            <SettingsChoiceGrid
              options={memberThemeChoiceOptions}
              value={preferences.memberAppTheme ? `member-${preferences.memberAppTheme}` : 'FOLLOW_CONSOLE'}
              onChange={(id) => {
                if (id === 'FOLLOW_CONSOLE') {
                  setMemberTheme(null)
                } else {
                  const key = id.replace('member-', '')
                  const opt = visibleThemeOptions.find((o) => o.key === key)
                  if (opt) handleMemberThemeSelect(opt)
                }
              }}
              onPreviewLocked={(choice) => {
                const key = choice.id.replace('member-', '')
                const opt = visibleThemeOptions.find((o) => o.key === key)
                if (opt?.cosmeticId)
                  onPreviewLockedItem?.({ kind: 'THEME', cosmeticId: opt.cosmeticId })
              }}
            />
          }
        />

        <SettingsRow
          stacked
          label={t('NLSSettingsLabelCanvasBackground')}
          hint={t('NLSSettingsHintCanvasBackground')}
          status={instantRows.statusFor('canvasBackground')}
          control={
            <SettingsChoiceGrid
              options={backgroundChoiceOptions}
              value={preferences.canvasBackground}
              onChange={(id) => {
                const opt = visibleBackgroundOptions.find((o) => o.key === id)
                if (opt) handleBackgroundSelect(opt)
              }}
              onPreviewLocked={(choice) => {
                const opt = visibleBackgroundOptions.find((o) => o.key === choice.id)
                if (opt?.cosmeticId)
                  onPreviewLockedItem?.({ kind: 'BACKGROUND', cosmeticId: opt.cosmeticId })
              }}
            />
          }
        />
      </SettingsSection>

      {/* 2. Focus Section */}
      <SettingsSection title={t('NLSSettingsSectionFocus')}>
        <SettingsRow
          stacked
          label={t('NLSSettingsLabelFocusSoundscape')}
          hint={t('NLSSettingsHintFocusSoundscape')}
          status={instantRows.statusFor('focusSoundscape')}
          control={
            <SettingsChoiceGrid
              options={soundscapeChoiceOptions}
              value={preferences.focusSoundscape}
              onChange={(id) => {
                const opt = visibleSoundscapeOptions.find((o) => o.key === id)
                if (opt) handleSoundscapeSelect(opt)
              }}
              onPreviewLocked={(choice) => {
                const opt = visibleSoundscapeOptions.find((o) => o.key === choice.id)
                if (opt?.cosmeticId)
                  onPreviewLockedItem?.({ kind: 'SOUNDSCAPE', cosmeticId: opt.cosmeticId })
              }}
            />
          }
        />
      </SettingsSection>

      {/* 3. Motion & Feedback Section */}
      <SettingsSection title={t('NLSSettingsSectionMotionAndFeedback')}>
        <SettingsRow
          label={t('NLSSettingsLabelAnimationProfile')}
          status={instantRows.statusFor('animationProfileDirect')}
          control={
            <div className="settingsSegmented">
              {(
                Object.keys(animationLabels) as Array<
                  ClientUserPreferences['animationProfile']
                >
              ).map((animationProfile) => (
                <Pressable
                  key={animationProfile}
                  aria-pressed={preferences.animationProfile === animationProfile}
                  className={
                    preferences.animationProfile === animationProfile
                      ? 'isActive'
                      : ''
                  }
                  onClick={() =>
                    void runSave('animationProfileDirect', () =>
                      updatePreferences({ animationProfile }),
                    )
                  }
                >
                  {animationLabels[animationProfile]}
                </Pressable>
              ))}
            </div>
          }
        />

        <SettingsRow
          label={t('NLSSettingsLabelReducedMotion')}
          hint={t('NLSSettingsHintReducedMotion')}
          status={instantRows.statusFor('animationProfile')}
          control={
            <Toggle
              checked={preferences.animationProfile === 'REDUCED_MOTION'}
              onChange={(next) =>
                void runSave('animationProfile', () =>
                  updatePreferences({
                    animationProfile: next ? 'REDUCED_MOTION' : 'FLUID',
                  }),
                )
              }
              aria-label={t('NLSSettingsLabelReducedMotion')}
            />
          }
        />

        <SettingsRow
          label={t('NLSSettingsLabelCelebration')}
          hint={t('NLSSettingsHintCelebration')}
          status={instantRows.statusFor('enableCelebration')}
          control={
            <Toggle
              checked={preferences.enableCelebration}
              onChange={(next) =>
                void runSave('enableCelebration', () =>
                  updatePreferences({ enableCelebration: next }),
                )
              }
              aria-label={t('NLSSettingsLabelCelebration')}
            />
          }
        />

        <SettingsRow
          label={t('NLSSettingsLabelHighFidelity')}
          hint={t('NLSSettingsHintHighFidelity')}
          status={instantRows.statusFor('highFidelityEffectsEnabled')}
          control={
            <Toggle
              checked={preferences.highFidelityEffectsEnabled}
              onChange={(next) =>
                void runSave('highFidelityEffectsEnabled', () =>
                  updatePreferences({ highFidelityEffectsEnabled: next }),
                )
              }
              aria-label={t('NLSSettingsLabelHighFidelity')}
            />
          }
        />

        <SettingsRow
          label={t('NLSSettingsLabelSoundCues')}
          hint={t('NLSSettingsHintSoundCues')}
          status={instantRows.statusFor('soundEnabled')}
          control={
            <Toggle
              checked={preferences.soundEnabled}
              onChange={(next) =>
                void runSave('soundEnabled', () =>
                  updatePreferences({ soundEnabled: next }),
                )
              }
              aria-label={t('NLSSettingsLabelSoundCues')}
            />
          }
        />

        <SettingsRow
          label={t('NLSSettingsLabelHaptics')}
          hint={t('NLSSettingsHintHaptics')}
          status={instantRows.statusFor('hapticEnabled')}
          control={
            <Toggle
              checked={preferences.hapticEnabled}
              onChange={(next) =>
                void runSave('hapticEnabled', () =>
                  updatePreferences({ hapticEnabled: next }),
                )
              }
              aria-label={t('NLSSettingsLabelHaptics')}
            />
          }
        />
      </SettingsSection>

      {/* 4. Layout Section */}
      <SettingsSection title={t('NLSSettingsSectionLayout')}>
        <SettingsRow
          label={t('NLSSettingsLabelRailCollapsedLabels')}
          hint={t('NLSSettingsHintRailCollapsedLabels')}
          status={instantRows.statusFor('railCollapsedShowLabels')}
          control={
            <Toggle
              checked={preferences.railCollapsedShowLabels}
              onChange={(next) =>
                void runSave('railCollapsedShowLabels', () =>
                  updatePreferences({ railCollapsedShowLabels: next }),
                )
              }
              aria-label={t('NLSSettingsLabelRailCollapsedLabels')}
            />
          }
        />

        <SettingsRow
          label={t('NLSSettingsLabelRailHoverPopup')}
          hint={t('NLSSettingsHintRailHoverPopup')}
          status={instantRows.statusFor('railAutoPopupOnHover')}
          control={
            <Toggle
              checked={preferences.railAutoPopupOnHover}
              onChange={(next) =>
                void runSave('railAutoPopupOnHover', () =>
                  updatePreferences({ railAutoPopupOnHover: next }),
                )
              }
              aria-label={t('NLSSettingsLabelRailHoverPopup')}
            />
          }
        />

        <SettingsRow
          label={t('NLSSettingsLabelCollapsingHeader')} /* Collapsing page headers */
          hint={t('NLSSettingsHintCollapsingHeader')}
          status={instantRows.statusFor('collapsingHeaderEnabled')}
          control={
            <Toggle
              checked={preferences.collapsingHeaderEnabled}
              onChange={(next) =>
                void runSave('collapsingHeaderEnabled', () =>
                  updatePreferences({ collapsingHeaderEnabled: next }),
                )
              }
              aria-label={t('NLSSettingsLabelCollapsingHeader')}
            />
          }
        />

        <SettingsRow
          label={t('NLSStatusMarkersLabel')}
          hint={t('NLSStatusMarkersHelp')}
          status={instantRows.statusFor('statusMarkers')}
          control={
            <div className="settingsSegmented">
              <Pressable
                aria-pressed={preferences.statusMarkers === 'SHORT'}
                className={
                  preferences.statusMarkers === 'SHORT' ? 'isActive' : ''
                }
                onClick={() =>
                  void runSave('statusMarkers', () =>
                    updatePreferences({ statusMarkers: 'SHORT' }),
                  )
                }
              >
                {t('NLSStatusMarkersOptionShort')}
              </Pressable>
              <Pressable
                aria-pressed={preferences.statusMarkers === 'LONG'}
                className={
                  preferences.statusMarkers === 'LONG' ? 'isActive' : ''
                }
                onClick={() =>
                  void runSave('statusMarkers', () =>
                    updatePreferences({ statusMarkers: 'LONG' }),
                  )
                }
              >
                {t('NLSStatusMarkersOptionLong')}
              </Pressable>
            </div>
          }
        />

        <SettingsRow
          label={t('NLSLoadingStyleLabel')}
          hint={t('NLSLoadingStyleHelp')}
          status={instantRows.statusFor('loadingStyle')}
          control={
            <div className="settingsSegmented">
              <Pressable
                aria-pressed={preferences.loadingStyle === 'CONSTELLATION'}
                className={
                  preferences.loadingStyle === 'CONSTELLATION' ? 'isActive' : ''
                }
                onClick={() =>
                  void runSave('loadingStyle', () =>
                    updatePreferences({ loadingStyle: 'CONSTELLATION' }),
                  )
                }
              >
                {t('NLSLoadingStyleOptionConstellation')}
              </Pressable>
              <Pressable
                aria-pressed={preferences.loadingStyle === 'STARFIELD'}
                className={
                  preferences.loadingStyle === 'STARFIELD' ? 'isActive' : ''
                }
                onClick={() =>
                  void runSave('loadingStyle', () =>
                    updatePreferences({ loadingStyle: 'STARFIELD' }),
                  )
                }
              >
                {t('NLSLoadingStyleOptionStarfield')}
              </Pressable>
            </div>
          }
        />
      </SettingsSection>

      {/* 5. Calendar Section */}
      <SettingsSection title={t('NLSSettingsSectionCalendar')}>
        <SettingsRow
          stacked
          label={t('NLSSettingsLabelCalendarStyle')}
          hint={t('NLSSettingsHintCalendarStyle')}
          status={instantRows.statusFor('calendarStyle')}
          control={
            <SettingsRadioList
              name="calendarStyle"
              options={calendarStyleOptions}
              value={effectiveCalendarStyle}
              onChange={handleCalendarStyleSelect}
            />
          }
        />

        <SettingsRow
          stacked
          label={t('NLSSettingsLabelCalendarBoardsShown')}
          hint={t('NLSSettingsHintCalendarBoardsShown')}
          status={instantRows.statusFor('calendarBoardScope')}
          control={
            <SettingsRadioList
              name="calendarBoardScope"
              options={calendarBoardScopeOptions}
              value={effectiveBoardScopeMode}
              onChange={handleCalendarBoardScopeSelect}
            />
          }
        />
      </SettingsSection>
    </div>
  )
}
